import { useMutation } from '@tanstack/react-query';
import { Alert, Flex } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text, Title } from 'components/mvp-typography';
import { APIClinicBilling } from 'lib/api/firebase/apis/api-clinic-balance';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorList } from 'lib/utils/useErrorList';
import { useState } from 'react';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { useHistory } from 'react-router-dom';
import { ClinicRoutes } from 'routes';

import { useGetClinicAllInfo, useHasSubscription } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';

interface PaymentDetailsProps {
  redirectUrlOverride?: string;
}

function useGetStripeSessionLink() {
  return useMutation({
    mutationFn: APIClinicBilling.getStripeCheckoutPage
  });
}

// eslint-disable-next-line
export function PaymentDetails(props: PaymentDetailsProps) {
  const [stripeSessionLink, setStripeSessionLink] = useState<string | null>(
    null
  );
  const clinicianContext = useClinicianContext();
  const errorList = useErrorList();
  const getStripeSession = useGetStripeSessionLink();
  const history = useHistory();

  /**
   * Check if the organisation has a payment method set up
   */
  const hasPaymentMethodQuery = useHasPaymentMethod(
    clinicianContext.currentClinic
  );

  /**
   * Check if the organisation has an active subscription
   */
  const hasSubscriptionQuery = useHasSubscription(
    clinicianContext.currentClinic
  );

  /**
   * Get basic clinic info to determine customerType
   */
  const { clinicInfo, query: clinicInfoQuery } = useGetClinicAllInfo(
    clinicianContext.currentClinic
  );

  useQueryErrorNotifier(hasPaymentMethodQuery, errorList, 'HasPaymentMethod');
  useQueryErrorNotifier(hasSubscriptionQuery, errorList, 'HasSubscription');
  useQueryErrorNotifier(clinicInfoQuery, errorList, 'GetClinicAllInfo');

  function handleEdit() {
    const hasSubscription =
      hasSubscriptionQuery.data?.data.hasPaymentMethod === true;
    const customerType = clinicInfo?.clinic.customerType;

    if (customerType === 'standard' && !hasSubscription) {
      history.push(ClinicRoutes.ClinicBillingSubscription);
      return;
    }

    getStripeSession.mutate(
      {
        clinicId: clinicianContext.currentClinic,
        redirectUrl: props.redirectUrlOverride ?? window.location.href
      },
      {
        onSuccess: out => {
          console.debug(out);
          setStripeSessionLink(out.data);
          window.open(out.data, '_blank');
        },
        onError(error) {
          errorList.addError(
            resolveError(error),
            'mutation',
            'Failed when opening the billing details form'
          );
        }
      }
    );
  }
  const hasPaymentMethod =
    hasPaymentMethodQuery.data?.data.hasPaymentMethod === true;
  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between">
        <div>
          <Title level={3}>Payment details</Title>
          <Text>
            A new page will open to manage your payment method and review invoice history.
          </Text>
          {stripeSessionLink && (
            <Text>
              Page didn&apos;t open?{' '}
              <a
                href={stripeSessionLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>
            </Text>
          )}
        </div>
        <div>
          <BaseButton
            disabled={
              getStripeSession.isPending ||
              hasPaymentMethodQuery.isLoading ||
              hasSubscriptionQuery.isLoading ||
              clinicInfoQuery.isLoading ||
              getStripeSession.isError
            }
            loading={
              hasPaymentMethodQuery.isLoading ||
              hasSubscriptionQuery.isLoading ||
              clinicInfoQuery.isLoading ||
              getStripeSession.isPending
            }
            onClick={handleEdit}
          >
            {hasPaymentMethod ? 'Edit' : 'Setup'}
          </BaseButton>
        </div>
      </Flex>

      {hasPaymentMethodQuery.data?.data.hasPaymentMethod === false && (
        <Alert
          showIcon
          type="info"
          message={
            <>
              <Text style={{ fontWeight: 'bold' }}>
                No payment method has been configured for this organisation.
              </Text>
            </>
          }
          description={
            <>
              <Text>
                You must complete the billing setup before purchasing assessments.
              </Text>
            </>
          }
        />
      )}

      {hasPaymentMethod && (
        <Alert
          showIcon
          type="info"
          message={
            <>
              <Text>
                Have a question about pricing, or want to change your subscription?{' '}
                <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>Contact us</a>.
              </Text>
            </>
          }
        />
      )}

      <ErrorListDisplay errorList={errorList} />
    </Flex>
  );
}
